/**
 * Loud in-app order alarm for a focused Hub screen: a repeating two-tone square-wave beep plus
 * vibration, until the operator acknowledges it. Browsers only allow sound after a tap on the
 * page, so the first tap unlocks the audio context.
 */

const BEEP_SECONDS = 0.22;
const CYCLE_MS = 1400;
const MAX_RING_MS = 15 * 60 * 1000;
const VIBRATION = [500, 150, 500];

let audioContext: AudioContext | null = null;
let ringTimer: ReturnType<typeof setInterval> | null = null;
let ringStartedAt = 0;

const AudioContextClass = (): typeof AudioContext | undefined =>
  typeof window === 'undefined'
    ? undefined
    : window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;

/** Call from a tap handler; resolves whether sound can play now. */
export const unlockHubAlarmAudio = async () => {
  const Context = AudioContextClass();
  if (!Context) {
    return false;
  }
  // iOS: a "playback" audio session plays the alarm even with the silent switch on (Safari 17+).
  const session = (navigator as Navigator & { audioSession?: { type: string } }).audioSession;
  if (session && session.type !== 'playback') {
    try {
      session.type = 'playback';
    } catch {
      // Older Safari: the alarm follows the silent switch.
    }
  }
  if (!audioContext) {
    audioContext = new Context();
  }
  if (audioContext.state === 'suspended') {
    await audioContext.resume().catch(() => undefined);
  }
  return audioContext.state === 'running';
};

export const isHubAlarmAudioReady = () => audioContext?.state === 'running';

const beep = (context: AudioContext, startAt: number, frequency: number) => {
  const oscillator = context.createOscillator();
  const gain = context.createGain();
  oscillator.type = 'square';
  oscillator.frequency.setValueAtTime(frequency, startAt);
  gain.gain.setValueAtTime(0.0001, startAt);
  gain.gain.exponentialRampToValueAtTime(0.9, startAt + 0.02);
  gain.gain.setValueAtTime(0.9, startAt + BEEP_SECONDS - 0.03);
  gain.gain.exponentialRampToValueAtTime(0.0001, startAt + BEEP_SECONDS);
  oscillator.connect(gain).connect(context.destination);
  oscillator.start(startAt);
  oscillator.stop(startAt + BEEP_SECONDS + 0.01);
};

const ringOnce = () => {
  if (audioContext && audioContext.state === 'running') {
    const now = audioContext.currentTime;
    beep(audioContext, now, 988);
    beep(audioContext, now + 0.28, 1319);
    beep(audioContext, now + 0.56, 988);
    beep(audioContext, now + 0.84, 1319);
  }
  if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
    navigator.vibrate(VIBRATION);
  }
};

/**
 * A short, softer two-note chime played once (for example the start-of-day briefing): it draws
 * attention without the repeating alarm reserved for a new order that needs someone now.
 */
const chime = (notes: ReadonlyArray<readonly [number, number]>, volume: number) => {
  if (!audioContext || audioContext.state !== 'running') {
    return false;
  }
  const context = audioContext;
  const now = context.currentTime;
  notes.forEach(([offset, frequency]) => {
    const startAt = now + offset;
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(frequency, startAt);
    gain.gain.setValueAtTime(0.0001, startAt);
    gain.gain.exponentialRampToValueAtTime(volume, startAt + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, startAt + 0.32);
    oscillator.connect(gain).connect(context.destination);
    oscillator.start(startAt);
    oscillator.stop(startAt + 0.34);
  });
  return true;
};

export const chimeHubAlert = () =>
  chime(
    [
      [0, 660],
      [0.18, 880],
      [0.7, 660],
      [0.88, 880],
    ],
    0.6,
  );

/**
 * One soft two-note chime: an alert for another of the operator's facilities (it does not ring
 * here). False when page sound is still locked.
 */
export const softChimeHubAlert = () =>
  chime(
    [
      [0, 784],
      [0.16, 1047],
    ],
    0.25,
  );

/**
 * The real alarm sound, a few times (alert setup's sound test on computers). Independent of the
 * ringing state, so it never stops or masks a real alarm. False when page sound is still locked.
 */
export const sampleHubAlarm = (times = 3) => {
  if (!audioContext || audioContext.state !== 'running') {
    return false;
  }
  for (let index = 0; index < times; index += 1) {
    setTimeout(ringOnce, index * CYCLE_MS);
  }
  return true;
};

export const isHubAlarmRinging = () => ringTimer !== null;

/** Starts the alarm; a new alert while it loops gives it the full time again (MAX_RING_MS). */
export const startHubAlarm = () => {
  ringStartedAt = Date.now();
  if (ringTimer) {
    return;
  }
  ringOnce();
  ringTimer = setInterval(() => {
    if (Date.now() - ringStartedAt > MAX_RING_MS) {
      stopHubAlarm();
      return;
    }
    ringOnce();
  }, CYCLE_MS);
};

export const stopHubAlarm = () => {
  if (ringTimer) {
    clearInterval(ringTimer);
    ringTimer = null;
  }
  if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
    navigator.vibrate(0);
  }
};
